import {
    AcademyTrainingsListingSortSlug,
    type AcademyTrainingsListingSort
} from '~/types/academy/trainings-listing-sort';
import { AcademyTrainingsListingTab } from '~/types/academy/trainings-listing-tab';

/** Default page for academy trainings listing requests. */
export const ACADEMY_TRAININGS_LISTING_DEFAULT_PAGE = 1;

/** Default page size for academy trainings listing (UI + merged listing endpoint). */
export const ACADEMY_TRAININGS_LISTING_DEFAULT_PAGE_SIZE = 10;

/** Maximum number of items displayed (and fetched) by the trainings carousel. */
export const ACADEMY_TRAININGS_CAROUSEL_MAX_ITEMS = 10;

/** Maximum number of related trainings shown on the academy detail page. */
export const ACADEMY_RELATED_TRAININGS_MAX_ITEMS = 8;

/**
 * Upper bound for course IDs per manual carousel request. All of them are enriched before the carousel
 * limit applies, so this bounds the Docebo calls a single request can cause.
 */
export const ACADEMY_MANUAL_TRAININGS_MAX_COURSE_IDS = 50;

/** Batch size when fetching all Docebo catalog items server-side for merging/sorting. */
export const ACADEMY_DOCEBO_CATALOG_FETCH_PAGE_SIZE = 100;
/** Safeguard in case of infinite loop */
export const ACADEMY_DOCEBO_CATALOG_MAX_PAGES = 100;

export const OPEN_STATES: DoceboCourseStatus[] = ['subscribed', 'in_progress'];
export const IN_PROGRESS_STATE: DoceboCourseStatus = 'in_progress';

const filterFields = [
    {
        name: 'trainingType',
        canonical: true
    },
    {
        name: 'product',
        canonical: true
    },
    {
        name: 'learningFormat',
        canonical: true
    },
    {
        name: 'status',
        canonical: false
    },
    {
        name: 'type',
        canonical: false
    },
    {
        name: 'bonusPoints',
        canonical: false
    }
] as const;

/** Fields that are canonicalized for the trainings listing. */
export const CANONICAL_FILTER_FIELDS = filterFields.filter((field) => field.canonical).map((field) => field.name);
/** Fields that are used for filtering the trainings listing. */
export const FILTER_FIELDS = filterFields.map((field) => field.name);

/**
 * Filter codes without a checkbox group in the filter panel. The tabs in the toolbar set `status`.
 * Only the carousels use `type`. `bonusPoints` is not relevant for filtering.
 */
const HIDDEN_FILTER_CODES = [
    'status',
    'type',
    'bonusPoints'
] as const satisfies readonly (typeof FILTER_FIELDS)[number][];

export const isHiddenFilterCode = (code: string): boolean =>
    HIDDEN_FILTER_CODES.some((hiddenCode) => hiddenCode === code);

type AcademyTrainingsListingUrlFilterCode = Exclude<
    (typeof FILTER_FIELDS)[number],
    (typeof HIDDEN_FILTER_CODES)[number]
>;

/** Filter codes that the trainings listing writes to the URL. The URL parameter has the same name as the code. */
export const TRAININGS_LISTING_URL_FILTER_CODES = FILTER_FIELDS.filter(
    (code): code is AcademyTrainingsListingUrlFilterCode => !isHiddenFilterCode(code)
);

export const CATALOG_SORT_KEYS = ['createdAt', 'title', 'points', 'startsAt'] as const;

/** The sort key and direction for each value of the `sort` URL parameter. */
export const ACADEMY_TRAININGS_LISTING_SORTS: Readonly<
    Record<AcademyTrainingsListingSortSlug, AcademyTrainingsListingSort>
> = {
    [AcademyTrainingsListingSortSlug.Newest]: { key: 'createdAt', direction: 'desc' },
    [AcademyTrainingsListingSortSlug.Oldest]: { key: 'createdAt', direction: 'asc' },
    [AcademyTrainingsListingSortSlug.EventDate]: { key: 'startsAt', direction: 'asc' },
    [AcademyTrainingsListingSortSlug.NameAscending]: { key: 'title', direction: 'asc' },
    [AcademyTrainingsListingSortSlug.NameDescending]: { key: 'title', direction: 'desc' },
    [AcademyTrainingsListingSortSlug.MostPoints]: { key: 'points', direction: 'desc' }
};

export const DEFAULT_ACADEMY_TRAININGS_LISTING_SORT_SLUG = AcademyTrainingsListingSortSlug.Newest;

export const DEFAULT_ACADEMY_TRAININGS_LISTING_TAB = AcademyTrainingsListingTab.All;

export const ACADEMY_REQUEST_TIMEOUT = 10_000;

export const ACADEMY_AGGREGATED_REQUEST_TIMEOUT = 30_000;

/** Key prefix shared by the fetches carrying course status, so a refresh can find them. */
export const ACADEMY_COURSE_DATA_KEY_PREFIX = 'academy-course-data';

/**
 * The trainings listing's namespace under that prefix, one entry per loaded page. Nested so a
 * refresh picks the pages up with everything else, and can still tell them apart: they are the only
 * ones it narrows by content instead of refetching outright.
 */
export const ACADEMY_TRAININGS_LISTING_KEY_PREFIX = `${ACADEMY_COURSE_DATA_KEY_PREFIX}:listing`;
export const ACADEMY_TRAININGS_RELATED_KEY_PREFIX = `${ACADEMY_COURSE_DATA_KEY_PREFIX}:related`;
export const ACADEMY_TRAININGS_MANUAL_KEY_PREFIX = `${ACADEMY_COURSE_DATA_KEY_PREFIX}:manual`;
export const ACADEMY_TRAININGS_SINGLE_KEY_PREFIX = `${ACADEMY_COURSE_DATA_KEY_PREFIX}:single`;
